import { Box, Text, TextProps } from '@chakra-ui/react'
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react'

type RunningTextLabelProps = TextProps & {
  maxWidth?: number | string
  children?: React.ReactNode
}

export const RunningTextLabel = forwardRef<HTMLDivElement, RunningTextLabelProps>(
  ({ children, maxWidth, ...textProps }, forwardedRef) => {
    const [outerWidth, setOuterWidth] = useState(0)
    const [innerWidth, setInnerWidth] = useState(0)
    const localOuterRef = useRef<HTMLDivElement | null>(null)
    const innerRef = useRef<HTMLSpanElement>(null)
    const [isScrolling, setIsScrolling] = useState(false)

    const beginScrolling = useCallback(() => {
      if (innerRef.current && localOuterRef.current) {
        // eslint-disable-next-line functional/immutable-data
        innerRef.current.style.marginLeft =
          outerWidth - innerWidth - 15 + 'px'
        // eslint-disable-next-line functional/immutable-data
        innerRef.current.style.transitionDuration =
          (innerWidth - outerWidth) / 100 + 's'
      }
    }, [outerWidth, innerWidth])

    const mouseEnter = useCallback(() => {
      if (
        innerRef.current &&
        localOuterRef.current &&
        innerRef.current.offsetWidth > localOuterRef.current.offsetWidth
      ) {
        setIsScrolling(true)
        beginScrolling()
      }
    }, [beginScrolling])

    const resetPosition = useCallback(() => {
      if (innerRef.current) {
        // eslint-disable-next-line functional/immutable-data
        innerRef.current.style.marginLeft = '0'
        // eslint-disable-next-line functional/immutable-data
        innerRef.current.style.transitionDuration = '0s'
        setIsScrolling(false)
      }
    }, [])

    useEffect(() => {
      setInnerWidth(innerRef.current?.offsetWidth ?? 0)
      setOuterWidth(localOuterRef.current?.offsetWidth ?? 0)
    }, [innerRef.current?.offsetWidth, localOuterRef.current?.offsetWidth])

    return (
      <Box
        display="block"
        lineHeight={textProps.lineHeight}
        maxWidth={maxWidth}
        onMouseEnter={mouseEnter}
        onMouseLeave={resetPosition}
        overflow="hidden"
        ref={(node) => {
          // eslint-disable-next-line functional/immutable-data
          localOuterRef.current = node

          if (typeof forwardedRef === 'function') {
            forwardedRef(node)
          } else if (forwardedRef) {
            // eslint-disable-next-line functional/immutable-data
            forwardedRef.current = node
          }
        }}
        textOverflow={isScrolling ? 'auto' : 'ellipsis'}
        whiteSpace="nowrap"
      >
        <Text
          as="span"
          ref={innerRef}
          transitionTimingFunction="linear"
          {...textProps}
        >
          {children}
        </Text>
      </Box>
    )
  }
)
